import React, { useState } from 'react';
import { X, ChevronDown, ChevronUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * RouterComparisonSheet component
 * Displays a detailed comparison of the two router models
 */
const RouterComparisonSheet = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('specifications');
  const [expandedSections, setExpandedSections] = useState({
    hardware: true,
    wireless: true,
    software: true,
    others: true
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Animation variants
  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.3 } },
    exit: { opacity: 0, transition: { duration: 0.2 } }
  };

  const sheetVariants = {
    hidden: { y: '100%' },
    visible: { y: 0, transition: { type: 'spring', damping: 30, stiffness: 300 } },
    exit: { y: '100%', transition: { duration: 0.3 } }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={onClose}
          />
          
          {/* Comparison Sheet */}
          <motion.div
            className="fixed inset-x-0 bottom-0 top-16 bg-gray-900 z-50 rounded-t-xl overflow-hidden flex flex-col"
            variants={sheetVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-800">
              <h2 className="text-xl font-bold text-white">Router Specifications Comparison</h2>
              <button
                className="p-2 rounded-full hover:bg-gray-800 transition-colors"
                onClick={onClose}
              >
                <X className="h-6 w-6 text-gray-400" />
              </button>
            </div>
            
            {/* Router Selection */}
            <div className="grid grid-cols-2 gap-0.5 bg-gray-800 p-0.5 rounded-lg mx-4 mt-4">
              <div className={`p-4 rounded-lg text-center ${activeTab === 'overview' ? 'bg-purple-900/50 text-white' : 'bg-gray-900 text-gray-400'}`}>
                <button 
                  className="w-full h-full font-medium"
                  onClick={() => setActiveTab('overview')}
                >
                  Overview
                </button>
              </div>
              <div className={`p-4 rounded-lg text-center ${activeTab === 'specifications' ? 'bg-purple-900/50 text-white' : 'bg-gray-900 text-gray-400'}`}>
                <button 
                  className="w-full h-full font-medium"
                  onClick={() => setActiveTab('specifications')}
                >
                  Specifications
                </button>
              </div>
            </div>
            
            {/* Router Models */}
            <div className="grid grid-cols-3 px-4 pt-6 pb-2">
              <div className="col-span-1"></div>
              <div className="col-span-1 flex flex-col items-center">
                <div className="w-24 h-24 mb-2">
                  <img 
                    src="https://apextech.group/elements-mesh-router-1.png" 
                    alt="EX141 Router"
                    className="w-full h-full object-contain"
                  />
                </div>
                <h3 className="text-lg font-semibold text-white">EX141</h3>
                <p className="text-sm text-gray-400">100Mbps and Below</p>
              </div>
              <div className="col-span-1 flex flex-col items-center">
                <div className="w-24 h-24 mb-2">
                  <img 
                    src="https://apextech.group/elements-mesh-router-1.png" 
                    alt="EX511 Router"
                    className="w-full h-full object-contain"
                  />
                </div>
                <h3 className="text-lg font-semibold text-white">EX511</h3>
                <p className="text-sm text-gray-400">Above 100Mbps</p>
              </div>
            </div>
            
            {/* Content Area */}
            <div className="flex-1 overflow-y-auto px-4 pb-8">
              {activeTab === 'overview' ? (
                <div className="grid grid-cols-3 gap-4 mt-4">
                  <div className="col-span-1"></div>
                  <div className="col-span-1 bg-gray-800/50 rounded-lg p-4">
                    <h4 className="text-purple-400 font-semibold mb-2">EX141 AX1500</h4>
                    <p className="text-gray-300 text-sm">
                      1.5 Gbps WiFi Speeds: Enjoy faster browsing, streaming, and downloading across two bands.
                    </p>
                    <ul className="mt-3 space-y-2 text-sm text-gray-300">
                      <li className="flex items-start">
                        <span className="text-purple-400 mr-2">•</span>
                        <span>Connect More, Stay Fast: Equipped with the latest WiFi 6 wireless technology</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-purple-400 mr-2">•</span>
                        <span>Extensive Coverage: Beamforming and four antennas combine to deliver focused reception</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-purple-400 mr-2">•</span>
                        <span>EasyMesh Compatible: Provides whole home WiFi coverage with standard mesh technology</span>
                      </li>
                    </ul>
                  </div>
                  <div className="col-span-1 bg-gray-800/50 rounded-lg p-4">
                    <h4 className="text-purple-400 font-semibold mb-2">EX511 AX3000</h4>
                    <p className="text-gray-300 text-sm">
                      3.0 Gbps WiFi Speeds: Experience ultra-fast browsing, streaming, and downloading across two bands.
                    </p>
                    <ul className="mt-3 space-y-2 text-sm text-gray-300">
                      <li className="flex items-start">
                        <span className="text-purple-400 mr-2">•</span>
                        <span>High-Performance WiFi 6: Advanced technology for faster speeds and greater capacity</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-purple-400 mr-2">•</span>
                        <span>Superior Coverage: Enhanced beamforming and four high-performance antennas</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-purple-400 mr-2">•</span>
                        <span>EasyMesh Compatible: Create a seamless mesh network throughout your home</span>
                      </li>
                    </ul>
                  </div>
                </div>
              ) : (
                <div className="mt-4">
                  {/* Hardware Features */}
                  <div className="mb-4 border-b border-gray-800 pb-2">
                    <div 
                      className="flex items-center justify-between cursor-pointer py-2"
                      onClick={() => toggleSection('hardware')}
                    >
                      <h3 className="text-lg font-semibold text-white">HARDWARE FEATURES</h3>
                      {expandedSections.hardware ? (
                        <ChevronUp className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                    
                    {expandedSections.hardware && (
                      <div className="mt-2">
                        <div className="grid grid-cols-3 border-b border-gray-800 py-3">
                          <div className="col-span-1 text-gray-400">Interface</div>
                          <div className="col-span-1 text-gray-300 text-sm">
                            1 10/100/1000 Mbps RJ45 WAN Port<br />
                            3 10/100/1000 Mbps RJ45 LAN Ports
                          </div>
                          <div className="col-span-1 text-gray-300 text-sm">
                            1× 10/100/1000 Mbps RJ45 WAN Port<br />
                            4× 10/100/1000 Mbps RJ45 LAN Ports
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-3 border-b border-gray-800 py-3">
                          <div className="col-span-1 text-gray-400">Button</div>
                          <div className="col-span-1 text-gray-300 text-sm">
                            Reset Button, WPS/Wi-Fi Button
                          </div>
                          <div className="col-span-1 text-gray-300 text-sm">
                            Power On/Off Button, Reset Button, Wi-Fi/WPS Button
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-3 border-b border-gray-800 py-3">
                          <div className="col-span-1 text-gray-400">External Power Supply</div>
                          <div className="col-span-1 text-gray-300 text-sm">12V/1A</div>
                          <div className="col-span-1 text-gray-300 text-sm">12V/1.5A</div>
                        </div>
                        
                        <div className="grid grid-cols-3 border-b border-gray-800 py-3">
                          <div className="col-span-1 text-gray-400">Dimensions (W x D x H)</div>
                          <div className="col-span-1 text-gray-300 text-sm">
                            7.2 × 3.7 × 1.3 in<br />
                            (182.7 × 94 × 33.3 mm)
                          </div>
                          <div className="col-span-1 text-gray-300 text-sm">
                            10.2 × 5.3 × 1.5 in<br />
                            (260.2 × 135.0 × 41.6 mm)
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-3 py-3">
                          <div className="col-span-1 text-gray-400">Antenna</div>
                          <div className="col-span-1 text-gray-300 text-sm">
                            Four External antennas
                          </div>
                          <div className="col-span-1 text-gray-300 text-sm">
                            Four high-performance external antennas
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Wireless Features */}
                  <div className="mb-4 border-b border-gray-800 pb-2">
                    <div 
                      className="flex items-center justify-between cursor-pointer py-2"
                      onClick={() => toggleSection('wireless')}
                    >
                      <h3 className="text-lg font-semibold text-white">WIRELESS FEATURES</h3>
                      {expandedSections.wireless ? (
                        <ChevronUp className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                    
                    {expandedSections.wireless && (
                      <div className="mt-2">
                        <div className="grid grid-cols-3 border-b border-gray-800 py-3">
                          <div className="col-span-1 text-gray-400">Wireless Standards</div>
                          <div className="col-span-1 text-gray-300 text-sm">
                            IEEE 802.11ax/ac/n/a 5 GHz<br />
                            IEEE 802.11n/g/b 2.4 GHz
                          </div>
                          <div className="col-span-1 text-gray-300 text-sm">
                            IEEE 802.11a/n/ac/ax 5 GHz<br />
                            IEEE 802.11b/g/n/ax 2.4 GHz
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-3 border-b border-gray-800 py-3">
                          <div className="col-span-1 text-gray-400">Frequency</div>
                          <div className="col-span-1 text-gray-300 text-sm">
                            2.4 GHz and 5 GHz
                          </div>
                          <div className="col-span-1 text-gray-300 text-sm">
                            2.4 GHz and 5 GHz
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-3 border-b border-gray-800 py-3">
                          <div className="col-span-1 text-gray-400">Signal Rate</div>
                          <div className="col-span-1 text-gray-300 text-sm">
                            1201 Mbps on 5 GHz<br />
                            300 Mbps on 2.4 GHz
                          </div>
                          <div className="col-span-1 text-gray-300 text-sm">
                            AX3000<br />
                            5 GHz: Up to 2402 Mbps<br />
                            2.4 GHz: Up to 574 Mbps
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-3 border-b border-gray-800 py-3">
                          <div className="col-span-1 text-gray-400">Wireless Functions</div>
                          <div className="col-span-1 text-gray-300 text-sm">
                            Enable/Disable Wireless Radio, WMM, Wireless Statistics, Band Steering
                          </div>
                          <div className="col-span-1 text-gray-300 text-sm">
                            Enable/Disable Wireless Radio, WMM, Wireless Statistics
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-3 border-b border-gray-800 py-3">
                          <div className="col-span-1 text-gray-400">Wireless Security</div>
                          <div className="col-span-1 text-gray-300 text-sm">
                            WPA/WPA2/WPA3 encryptions
                          </div>
                          <div className="col-span-1 text-gray-300 text-sm">
                            WPA/WPA2/WPA3 encryptions
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-3 py-3">
                          <div className="col-span-1 text-gray-400">Mesh Technology</div>
                          <div className="col-span-1 text-gray-300 text-sm">
                            EasyMesh Compatible, and support 802.11k/v/r
                          </div>
                          <div className="col-span-1 text-gray-300 text-sm">
                            EasyMesh compatible, and support 802.11k/v/r
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Software Features */}
                  <div className="mb-4 border-b border-gray-800 pb-2">
                    <div 
                      className="flex items-center justify-between cursor-pointer py-2"
                      onClick={() => toggleSection('software')}
                    >
                      <h3 className="text-lg font-semibold text-white">SOFTWARE FEATURES</h3>
                      {expandedSections.software ? (
                        <ChevronUp className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                    
                    {expandedSections.software && (
                      <div className="mt-2">
                        <div className="grid grid-cols-3 border-b border-gray-800 py-3">
                          <div className="col-span-1 text-gray-400">Advanced Features</div>
                          <div className="col-span-1 text-gray-300 text-sm">
                            Muti-SSID, Guest Network, QoS (Quality of Service), TX Beamforming, MU-MIMO, OFDMA, Airtime Fairness
                          </div>
                          <div className="col-span-1 text-gray-300 text-sm">
                            TX Beamforming, MU-MIMO, DL/UL OFDMA, Airtime Fairness
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-3 border-b border-gray-800 py-3">
                          <div className="col-span-1 text-gray-400">Operating Modes</div>
                          <div className="col-span-1 text-gray-300 text-sm">
                            Router, Access Point
                          </div>
                          <div className="col-span-1 text-gray-300 text-sm">
                            Router, Access Point
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-3 border-b border-gray-800 py-3">
                          <div className="col-span-1 text-gray-400">WAN Type</div>
                          <div className="col-span-1 text-gray-300 text-sm">
                            Dynamic IP/Static IP/PPPoE/PPTP/L2TP (Multi-EWAN)
                          </div>
                          <div className="col-span-1 text-gray-300 text-sm">
                            Dynamic IP/Static IP/PPPoE/PPTP/L2TP(Multi-EWAN)
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-3 border-b border-gray-800 py-3">
                          <div className="col-span-1 text-gray-400">Management Features</div>
                          <div className="col-span-1 text-gray-300 text-sm">
                            Local management: WebGUI management, Aginet app management<br />
                            Remote management: Aginet ACS, TR-069, TR-098, TR-181,TR-111, and TR-143 for service providers
                          </div>
                          <div className="col-span-1 text-gray-300 text-sm">
                            Local management: WebGUI, Aginet app (for end users)<br />
                            Remote management: Aginet ACS, TR-069, TR-098, TR-181, TR-369, TR-111,TR-143 for service providers<br />
                            Cloud management: TP-Link Aginet Unified Cloud (TAUC)
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-3 py-3">
                          <div className="col-span-1 text-gray-400">Access Control</div>
                          <div className="col-span-1 text-gray-300 text-sm">
                            Parental Controls, White List, Black List
                          </div>
                          <div className="col-span-1 text-gray-300 text-sm">
                            Parental Controls, White List, Black List
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Others */}
                  <div className="mb-4">
                    <div 
                      className="flex items-center justify-between cursor-pointer py-2"
                      onClick={() => toggleSection('others')}
                    >
                      <h3 className="text-lg font-semibold text-white">OTHERS</h3>
                      {expandedSections.others ? (
                        <ChevronUp className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                    
                    {expandedSections.others && (
                      <div className="mt-2">
                        <div className="grid grid-cols-3 border-b border-gray-800 py-3">
                          <div className="col-span-1 text-gray-400">Package Content</div>
                          <div className="col-span-1 text-gray-300 text-sm">
                            AX1500 Dual Band Gigabit Wi-Fi 6 Router EX141<br />
                            Power Adapter<br />
                            RJ45 Ethernet Cable<br />
                            Quick Installation Guide
                          </div>
                          <div className="col-span-1 text-gray-300 text-sm">
                            WiFi 6 Router EX511<br />
                            Power Adapter<br />
                            RJ45 Ethernet Cable<br />
                            Quick Installation Guide
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-3 border-b border-gray-800 py-3">
                          <div className="col-span-1 text-gray-400">Environment</div>
                          <div className="col-span-1 text-gray-300 text-sm">
                            Operating Temperature: 0℃–40℃ (32℉–104℉)<br />
                            Storage Temperature: -40℃–60℃ (-40℉–140℉)<br />
                            Operating Humidity: 10%–90% non-condensing<br />
                            Storage Humidity: 5%–90% non-condensing
                          </div>
                          <div className="col-span-1 text-gray-300 text-sm">
                            Operating Temperature: 0℃–40℃ (32℉–104℉)<br />
                            Storage Temperature: -40℃–60℃ (-40℉–140℉)<br />
                            Operating Humidity: 10%–90% non-condensing<br />
                            Storage Humidity: 5%–90% non-condensing
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default RouterComparisonSheet;